import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";
import { Input } from "../../components/ui/input";
import { Users, Search } from "lucide-react";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/customers")
      .then((r) => setCustomers(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = customers.filter((c) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (c.full_name || "").toLowerCase().includes(needle) ||
      (c.email || "").toLowerCase().includes(needle) ||
      (c.phone || "").toLowerCase().includes(needle) ||
      (c.drivers_license || "").toLowerCase().includes(needle)
    );
  });

  return (
    <AdminShell title="Customers">
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
          <Input
            placeholder="Search by name, email, phone, license…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-[#04060d] border-white/10 h-10 pl-9"
            data-testid="customer-search"
          />
        </div>
        <span className="label-eyebrow ml-auto" style={{ color: "var(--wakzy-gold)" }}>
          <Users className="w-3 h-3 inline mr-1" /> {filtered.length} customer{filtered.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="surface rounded-md overflow-hidden" data-testid="customers-table">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-slate-400 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Contact</th>
              <th className="px-4 py-3 text-left">License</th>
              <th className="px-4 py-3 text-left">Address</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                  Loading customers…
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((c) => (
                <tr key={c.id} className="hover:bg-white/[0.02]" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{c.full_name || "—"}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{c.id.slice(0, 8)}…</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">
                    <div>{c.email || "—"}</div>
                    <div className="text-xs text-slate-500">{c.phone || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300 font-mono text-xs">
                    {c.drivers_license || "—"}
                    {c.license_state && <span className="text-slate-500"> · {c.license_state}</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {[c.address, c.city, c.state, c.zip].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/customers/${c.id}`}
                      className="text-[#D4AF37] hover:text-[#f0c659] hover:underline text-xs"
                      data-testid={`customer-open-${c.id}`}
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                  No customers match &ldquo;{q}&rdquo;.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminShell>
  );
}
